import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as R from 'ramda';
import validationService from '../services/validation';
import authenticationService, { AuthenticateRequest, ExchangeTokenData } from '../services/authentication';
import RenewableToken, { TokenRenewalResult } from './token/RenewableToken';
import NullToken from './token/NullToken';
import ReAuthenticationModal from '../screens/authentication/ReAuthenticationModal';
import { CustomError } from '../common/errors';
import Result from '../common/result';
import RenewTokenCallbackCache from './token/RenewTokenCallbackCache';
import OneTimeToken from './token/OneTimeToken';
import { Token, TokenResult } from '../services/common/token';
import metadataCollector from '../services/metadata';
import sip from '../services/secureIdentityProvider';
import faceAuthentication from '../services/faceAuthentication';
import UserTokenSettable, { isUserTokenSettable } from '../services/common/userTokenSettable';
import { SignerContext } from './SignerContext';
import { MemoryRouterContext } from './MemoryRouterContext';
import { getCivicSignAuth } from '../utils/storage';
import Log from '../common/logging';

export type User = {
  did: string;
  publicKey: string;
};

export type UserResult = Result<CustomError, User>;
export type LoginResult = Result<CustomError>;
export type LoginDeviceTokenResult = Result<CustomError, ExchangeTokenData>;

export interface AuthenticationProps {
  getUserData(): Promise<UserResult>;
  performLogin(usingLedger: boolean): Promise<LoginResult>;
  loginWithDeviceLinkToken(linkToken: string): Promise<LoginDeviceTokenResult>;
  getCurrentToken(): Promise<TokenResult>;
  isLoggedIn(): boolean;
  token?: Token;
  user?: User;
  loggedIn: boolean;
  retrievingExistingAuth: boolean;
}

export const AuthenticationContext = createContext<AuthenticationProps>({} as AuthenticationProps);

const nullUser: User = {
  did: '',
  publicKey: '',
};

type AuthenticationProviderProps = {
  children: JSX.Element;
};

/**
 * Provider that adds the authentication context
 * provides functionality to handle the collection of keys and proof to authenticate the user against the api
 *
 * @param props
 * @constructor
 */
export function AuthenticationProvider(props: AuthenticationProviderProps): JSX.Element {
  const { children } = props;
  const { queryParams } = useContext(MemoryRouterContext);
  const { signer } = useContext(SignerContext);
  const [token, setToken] = useState<Token>(new NullToken());
  const [user, setUser] = useState<User>(nullUser);
  const [renewTokenCallbackCache] = useState<RenewTokenCallbackCache>(new RenewTokenCallbackCache());
  const [reAuthenticating, setReAuthenticating] = useState<boolean>(false);
  const tokenSettableServices: UserTokenSettable[] = [
    validationService,
    authenticationService,
    sip,
    faceAuthentication,
  ];
  const [retrievingExistingAuth, setRetrievingExistingAuth] = useState<boolean>(true);

  useEffect(() => {
    // eslint-disable-next-line array-callback-return
    tokenSettableServices.map((s) => {
      if (isUserTokenSettable(s)) {
        s.setToken(token);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // TODO: TECH-194 - Re-enable storing of the auth token once it can be done in a cookie
  // useEffect(() => {
  //   if (token && !(token instanceof NullToken || token instanceof OneTimeToken)) {
  //     Log.debug('AuthenticationProvider: Token set');
  //     token.getToken().then((tokenResult) => {
  //       Log.debug('AuthenticationProvider: storing token', tokenResult.success());
  //       storeCivicSignAuth(tokenResult.success());
  //     });
  //   }
  // }, [token]);

  /*
   * Return the auth token
   */
  function currentToken(): Promise<TokenResult> {
    return token.getToken();
  }

  async function getUserData(): Promise<UserResult> {
    if (!R.equals(user, nullUser)) {
      return Result.successful(user);
    }

    if (!signer) return Result.failed(new CustomError('Signer not available'));
    const didResult = await signer?.requestDid();

    if (didResult.isFailed()) {
      didResult.logError('AuthenticationProvider: Error requesting DID');
      return Result.failed(didResult.failure());
    }

    const userResult: User = {
      did: didResult.success().did,
      publicKey: didResult.success().publicKey,
    };
    metadataCollector.updateMetadata({ did: userResult.did, wallet: userResult.publicKey });

    setUser(userResult);
    return Result.successful(userResult);
  }

  /** Authenticate the user and return a token */
  async function authenticate(usingLedger = false): Promise<TokenResult> {
    const authenticateRequest: AuthenticateRequest = {} as AuthenticateRequest;
    const userResult = await getUserData();
    if (userResult.isFailed()) {
      return Result.failed(userResult.failure());
    }

    const userData = userResult.success();
    authenticateRequest.did = userData.did;
    authenticateRequest.address = userData.publicKey;
    authenticateRequest.chain = queryParams.chain as 'solana' | 'ethereum';
    authenticateRequest.network = queryParams.chainNetwork as string;

    if (!signer) return Result.failed(new CustomError('Signer not available'));

    let nonce;
    // only the solana ledger app doesn't support signing a message currently
    if (!usingLedger || queryParams.chain !== 'solana') {
      nonce = (await authenticationService.getNonce()).success();
      authenticateRequest.nonceTimestamp = nonce.timestamp;
    }

    const signedProofResult = await signer.requestProof(nonce);

    if (signedProofResult.isSuccessful()) {
      authenticateRequest.proof = signedProofResult.success();
    } else {
      signedProofResult.logError('AuthenticationProvider: Error requesting signed proof');
      return Result.failed(signedProofResult.failure());
    }

    const authResult = await authenticationService.authenticate(authenticateRequest);
    if (authResult.isFailed()) {
      authResult.logError('AuthenticationProvider: Error authenticating user');
      return Result.failed(authResult.failure());
    }

    const { token: resultToken } = authResult.success();
    return Result.successful(resultToken);
  }

  /** Renew the token by performing an authentication and call back with the result */
  function renewToken(resultCallback: TokenRenewalResult): void {
    renewTokenCallbackCache.setCallback(resultCallback);
    setReAuthenticating(true);
  }

  const renewTokenConfirmed = async (): Promise<void> => {
    const authenticateResult = await authenticate();
    renewTokenCallbackCache.invoke(authenticateResult);
    setReAuthenticating(false);
  };

  /**
   * This method is responsible for getting the DID, public key and proof, adding to the Context state and getting a token from the api
   */
  async function performLogin(usingLedger = false): Promise<LoginResult> {
    const authenticateResult = await authenticate(usingLedger);
    if (authenticateResult.isFailed()) {
      return Result.failed(authenticateResult.failure());
    }
    const resultToken = authenticateResult.success();
    const renewableToken = new RenewableToken(resultToken, renewToken);
    setToken(renewableToken);
    return Result.noValueSuccess();
  }

  /**
   * This method exchanges the link token for an authentication token and seeds the user and token state from the resulting data
   */
  async function loginWithDeviceLinkToken(linkToken: string): Promise<LoginDeviceTokenResult> {
    const authResult = await authenticationService.authenticateExchangeToken({ exchangeToken: linkToken });
    if (authResult.isFailed()) {
      authResult.logError('AuthenticationProvider: Error exchanging authentication token');
      return Result.failed(authResult.failure());
    }

    const authenticatedData = authResult.success();
    const { context } = authenticatedData;
    setUser({
      did: context.did,
      publicKey: context.address,
    });
    const oneTimeToken = new OneTimeToken(authenticatedData.token);
    setToken(oneTimeToken);
    return Result.successful(context);
  }

  function isLoggedIn(): boolean {
    const tokenSet = !(token instanceof NullToken);
    return tokenSet;
  }

  const loggedIn = useMemo(() => {
    return !(token instanceof NullToken);
  }, [token]);

  const isAuthValid = useCallback(
    async (auth: string, inUser?: User): Promise<boolean> => {
      if (!inUser?.did) {
        return false;
      }
      try {
        const tokenInst = new OneTimeToken(auth);
        const tokenResult = await tokenInst.getToken();
        const decodedToken = tokenInst.getDecoded();
        if (tokenResult.isSuccessful() && decodedToken.sub === inUser?.did) {
          return true;
        }
      } catch (e) {
        Log.error('AuthenticationProvider: Error checking token validity', e as Error);
        return false;
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  useEffect(() => {
    const existingAuth = getCivicSignAuth();
    Log.debug('AuthenticationProvider: existingAuth exists', existingAuth);
    if (existingAuth && signer) {
      getUserData().then((userData) => {
        if (!userData.isSuccessful()) {
          return;
        }
        Log.debug('AuthenticationProvider: getUserData', userData);
        setUser(userData.success());
        isAuthValid(existingAuth, userData.success())
          .then((valid) => {
            if (valid) {
              Log.debug('AuthenticationProvider: is valid, retrieving and creating a new renewable token');
              const renewableToken = new RenewableToken(existingAuth, renewToken);
              setToken(renewableToken);
            }
          })
          .finally(() => {
            setRetrievingExistingAuth(false);
          });
      });
    }
    setRetrievingExistingAuth(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signer]);

  const value = useMemo(
    () => ({
      getUserData,
      performLogin,
      loginWithDeviceLinkToken,
      getCurrentToken: currentToken,
      isLoggedIn,
      user,
      token,
      loggedIn,
      retrievingExistingAuth,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getUserData, performLogin, loginWithDeviceLinkToken, currentToken, isLoggedIn, user, token],
  );

  return (
    <AuthenticationContext.Provider value={value}>
      <ReAuthenticationModal authenticate={renewTokenConfirmed} showModal={reAuthenticating} />
      {children}
    </AuthenticationContext.Provider>
  );
}

export const useAuthentication = (): AuthenticationProps => useContext(AuthenticationContext);
